import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Eine Shopify-Betreuung lohnt sich, wenn regelmäßig kleine Aufgaben anfallen: Anpassungen, neue Produkte, App-Fragen, laufende Verbesserungen. Statt eines teuren Vertrags mit Mindestlaufzeit reicht oft eine Abrechnung nach Stunden, bei der Sie nur zahlen, wenn wirklich etwas zu tun ist.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/shopify-shop-betreuen-lassen";
const TITLE = "Shopify-Shop betreuen lassen: Was kostet das?";
const DESCRIPTION =
  "Shopify-Betreuung ohne Mindestlaufzeit: was eine laufende Betreuung umfasst, was sie kostet und wann sie sich lohnt.";

const faqs = [
  {
    q: "Was umfasst eine Shop-Betreuung?",
    a: "Anpassungen am Theme, neue Inhalte und Produkte, Hilfe bei Apps, kleine Verbesserungen und schnelle Hilfe, wenn etwas nicht funktioniert.",
  },
  {
    q: "Was kostet eine laufende Betreuung?",
    a: "Bei mir gibt es keinen teuren Monatsvertrag. Anfallende Arbeit wird mit 110 € netto pro Stunde abgerechnet, nur wenn wirklich etwas zu tun ist.",
  },
  {
    q: "Brauche ich eine Betreuung, wenn Shopify doch alles hostet?",
    a: "Hosting und Updates übernimmt Shopify. Aber Inhalte, Anpassungen, Apps und Verbesserungen bleiben Ihre Aufgabe. Genau da hilft eine Betreuung.",
  },
  {
    q: "Lohnt sich das auch für kleine Shops?",
    a: "Ja, gerade dann. Statt selbst stundenlang zu probieren, ist eine Stunde Facharbeit oft günstiger und das Ergebnis besser.",
  },
];

export const Route = createFileRoute("/fragen/shopify-shop-betreuen-lassen")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Betreuung",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Shopify-Shop betreuen lassen: Was kostet das?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Betreuung"
      title="Shopify-Shop betreuen lassen"
      intro="Kurz gesagt: Betreuung ohne Mindestlaufzeit. Sie zahlen 110 € netto pro Stunde, nur wenn wirklich etwas zu tun ist."
      source="frage-shop-betreuung"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Was eine Betreuung abdeckt",
          p: [],
          list: [
            "Anpassungen am Theme und neue Abschnitte",
            "Produkte, Inhalte und Sortiment pflegen",
            "Apps prüfen, einrichten oder ersetzen",
            "Schnelle Hilfe, wenn etwas nicht funktioniert",
            "Laufende kleine Verbesserungen, die den Umsatz stärken",
          ],
        },
        {
          h: "Ohne Mindestlaufzeit",
          p: [
            "Viele Agenturen verkaufen Monatsverträge, egal ob etwas anfällt oder nicht. Bei mir zahlen Sie nur die tatsächlich geleistete Zeit. In ruhigen Monaten kostet die Betreuung nichts.",
          ],
        },
        {
          h: "Was Shopify selbst übernimmt",
          p: [
            "Hosting, Sicherheit und Systemupdates macht Shopify. Alles, was Ihren Shop individuell betrifft, bleibt bei Ihnen. Genau dabei unterstütze ich.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/fragen/was-kostet-ein-shopify-freelancer", label: "Was kostet ein Shopify Freelancer?" },
        { to: "/kontakt", label: "Kontakt" },
      ]}
    />
  );
}
